import type { Extensions } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import { FormNoteInsertion } from './formNoteInsertion';

/**
 * The one TipTap configuration for report narrative (Slice 19b, ADR-0002).
 *
 * It must produce only what the server accepts: nodes doc, paragraph,
 * text, hardBreak, heading (H2/H3), bulletList, orderedList, listItem;
 * marks bold, italic, underline, link; plus formNoteInsertion at the top
 * level (Slice 19c). Anything else is rejected with a
 * 400 on save, so StarterKit's other extensions are switched off here
 * rather than left for the server to refuse.
 *
 * Shared by the editor and the read-only preview so the two can't drift.
 */
export function narrativeExtensions(): Extensions {
  return [
    StarterKit.configure({
      // The section title is the H1 on the surrounding page.
      heading: { levels: [2, 3] },
      blockquote: false,
      code: false,
      codeBlock: false,
      strike: false,
      horizontalRule: false,
    }),
    Underline,
    FormNoteInsertion,
    Link.configure({
      openOnClick: false,
      HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      // Mirrors the server: only absolute http(s) and mailto links. This also
      // stops autolink and paste from creating anything the save would refuse.
      isAllowedUri: (url) => isAllowedLink(url),
    }),
  ];
}

/** Same rule the server applies to a link's href. */
export function isAllowedLink(url: string): boolean {
  try {
    const parsed = new URL(url.trim());
    return ['http:', 'https:', 'mailto:'].includes(parsed.protocol);
  } catch {
    return false;
  }
}

/**
 * Turns what an auditor types into the link prompt into an allowed href:
 * "example.com" becomes "https://example.com" and a bare address becomes
 * "mailto:". Returns null when it still isn't allowed.
 */
export function normaliseLink(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  if (isAllowedLink(trimmed)) return trimmed;

  const candidate = /^[^\s@/]+@[^\s@/]+\.[^\s@/]+$/.test(trimmed)
    ? `mailto:${trimmed}`
    : `https://${trimmed}`;
  return isAllowedLink(candidate) ? candidate : null;
}
